import { IconButton, Paper, Slide, Stack, Drawer } from '@mui/material';
import { useIntl } from 'react-intl';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import BatchMonitor from 'pages/knowledgeBase/batch/batchMonitor';
import DinaBatchExecutionHistory from 'pages/knowledgeBase/batch/dinaBatchExecutionHistory';
import MDBox from 'components/MDBox';
import { GridCards } from 'components/Cards/GridCards';
import {
  useDeleteBatchMutation,
  useExecuteBatchMutation,
  useGetBatchesByKnowledgeBaseCodeQuery
} from 'services/dinaBatchApi';
import IndeterminateLoading from 'components/Loading/IndeterminateLoading';
import { useNotification } from 'hook/useNotification';
import DinaBatchDetailsDrawer from 'pages/knowledgeBase/batch/dinaBatchDetailsDrawer';
import { useGetKnowledgeBaseQuery } from 'services/knowledgeBaseApi';
import { getBatchColor, getBatchIcon } from 'pages/knowledgeBase/batch/batchUtils';

/**
 * Component for displaying Knowledge Base Batch jobs.
 *
 * @component
 *
 * @example
 * <KnowledgeBaseBatch />
 *
 * @returns {React.Component} The Knowledge Base Batch component.
 */
export default function KnowledgeBaseBatch() {
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();
  const { knowledgeBaseId } = useParams();

  const [batchDrawerOpen, setBatchDrawerOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [monitoredBatch, setMonitoredBatch] = useState(null);
  const [currentJobExecutionId, setCurrentJobExecutionId] = useState(null);
  const [monitoringOpen, setMonitoringOpen] = useState(false);
  const [historyBatch, setHistoryBatch] = useState(null);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);

  const [deleteBatch] = useDeleteBatchMutation();
  const [executeBatch] = useExecuteBatchMutation();

  const { data: knowledgeBase, isLoading: knowledgeBaseLoading } =
    useGetKnowledgeBaseQuery(knowledgeBaseId);

  const {
    data: batches,
    isLoading: batchesLoading,
    error: batchesError
  } = useGetBatchesByKnowledgeBaseCodeQuery(knowledgeBase?.code, {
    skip: knowledgeBaseLoading || !knowledgeBase?.code
  });

  if (batchesLoading) {
    return (
      <IndeterminateLoading
        sx={{ width: '100%', color: '#125688' }}
        message={msg({ id: 'loading.batches' })}
      />
    );
  }

  if (batchesError) {
    try {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.batch.fetch.error' })
      });
    } catch {
      // Ignore exception
    }
  }

  const cardItems =
    batches?.map((batch) => ({
      id: batch.id,
      label: batch.batchType?.label || `Batch ${batch.id}`,
      description: batch.batchType?.description || msg({ id: 'batch.no.description' }),
      icon: getBatchIcon(batch.batchType?.code),
      color: getBatchColor(batch.batchType?.code)
    })) ?? [];

  const handleEdit = (id) => {
    setSelectedBatch(batches.find((batch) => batch.id === id));
    setBatchDrawerOpen(true);
  };

  const handleCreate = () => {
    setSelectedBatch(null);
    setBatchDrawerOpen(true);
  };

  const handleDelete = (id) => {
    deleteBatch(id)
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.batch.delete.success' })
        });
      })
      .catch((error) => {
        console.error(error);
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.batch.delete.error' })
        });
      });
  };

  const handleExecute = (id) => {
    setMonitoredBatch(null);
    setHistoryBatch(null);
    setHistoryDrawerOpen(false);

    // Immediately set up monitoring when execution starts
    const batch = batches.find((b) => b.id === id);
    if (!batch) {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.error.title' }),
        message: msg({ id: 'page.batch.not.found' })
      });
      return;
    }
    setCurrentJobExecutionId('pending');

    executeBatch(id)
      .unwrap()
      .then((response) => {
        // Update with real execution ID if provided in response
        if (response?.jobExecutionId) {
          displayBatchMonitoring(batch, response.jobExecutionId);
        }

        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.batch.execute.success' })
        });
      })
      .catch((error) => {
        if (error?.status === 409) {
          addNotification({
            type: 'warning',
            title: msg({ id: 'notification.warning.title' }),
            message: msg({ id: 'page.batch.execute.already.running' })
          });
          const jobExecutionId = error?.data?.jobExecutionId;
          if (jobExecutionId) {
            displayBatchMonitoring(batch, jobExecutionId);
          }
        } else {
          console.error(error);
          // Clear monitoring on error
          setMonitoredBatch(null);
          setCurrentJobExecutionId(null);
          setMonitoringOpen(false);
          addNotification({
            type: 'error',
            title: msg({ id: 'notification.error.title' }),
            message: msg({ id: 'page.batch.execute.error' })
          });
        }
      });
  };

  const handleSelect = (batchId) => {
    const batch = batches.find((batch) => batch.id === batchId);
    setSelectedBatch(batch);

    // Close monitoring if open and show history instead
    setMonitoringOpen(false);
    setMonitoredBatch(null);
    setCurrentJobExecutionId(null);

    // Show execution history for selected batch
    if (batch) {
      setHistoryBatch(batch);
      setHistoryDrawerOpen(true);
    }
  };

  const handlePlayBatch = (batchId) => {
    handleExecute(batchId);
    setSelectedBatch(batches.find((batch) => batch.id === batchId));
  };

  const handleCloseMonitoring = () => {
    setMonitoringOpen(false);
    setMonitoredBatch(null);
    setCurrentJobExecutionId(null);
  };

  const handleCloseHistoryDrawer = () => {
    setHistoryDrawerOpen(false);
    setHistoryBatch(null);
  };

  return (
    <>
      <MDBox sx={{ display: 'flex', flex: 1, gap: 2 }}>
        {/* Main content area */}
        <Paper
          elevation={2}
          sx={{
            flex: 1,
            borderRadius: '10px',
            p: 2,
            mb: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <GridCards
            defaultIcon="batch_prediction"
            items={cardItems}
            onCreate={handleCreate}
            onDelete={handleDelete}
            onUpdate={handleEdit}
            onSelect={handleSelect}
            onPlay={handlePlayBatch}
            canEdit={true}
          />
        </Paper>

        {/* Monitoring area - for active batch execution */}
        <Slide direction="left" in={monitoringOpen} mountOnEnter unmountOnExit timeout={300}>
          <Paper
            id="paperSlideMonitoring"
            elevation={2}
            sx={{
              display: 'flex',
              position: 'relative',
              borderRadius: '10px',
              mb: 1,
              minWidth: 450
            }}
          >
            <Stack sx={{ position: 'relative', width: '100%' }}>
              <IconButton
                onClick={handleCloseMonitoring}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  backgroundColor: 'background.paper',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              {monitoredBatch ? (
                <BatchMonitor
                  batch={monitoredBatch}
                  jobExecutionId={
                    currentJobExecutionId === 'pending' ? null : currentJobExecutionId
                  }
                />
              ) : null}
            </Stack>
          </Paper>
        </Slide>
      </MDBox>

      {/* Batch Details Drawer */}
      <DinaBatchDetailsDrawer
        drawerOpen={batchDrawerOpen}
        setDrawerOpen={setBatchDrawerOpen}
        batch={selectedBatch}
        knowledgeBase={knowledgeBase}
      />

      {/* Batch Execution History Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={historyDrawerOpen}
        onClose={handleCloseHistoryDrawer}
        ModalProps={{ keepMounted: false }}
        PaperProps={{
          sx: {
            width: 600,
            p: 2
          }
        }}
      >
        {historyBatch ? <DinaBatchExecutionHistory batch={historyBatch} /> : null}
      </Drawer>
    </>
  );

  function displayBatchMonitoring(batch, jobExecutionId) {
    // Close history drawer if open
    setHistoryDrawerOpen(false);
    setHistoryBatch(null);

    // Show monitoring
    setCurrentJobExecutionId(jobExecutionId);
    setMonitoredBatch(batch);
    setMonitoringOpen(true);
  }
}
